// ** React Imports
import { ElementType } from 'react'

// ** Next Imports
import Link from 'next/link'
import { useRouter } from 'next/router'

// ** MUI Imports
import Chip from '@mui/material/Chip'
import ListItem from '@mui/material/ListItem'
import { styled } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import Box, { BoxProps } from '@mui/material/Box'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemButton, { ListItemButtonProps } from '@mui/material/ListItemButton'

// ** Configs Import
import themeConfig from 'src/configs/themeConfig'

// ** Types
import { NavLink, NavGroup } from 'src/@core/layouts/types'
import { Settings } from 'src/@core/context/settingsContext'

// ** Custom Components Imports
import UserIcon from 'src/layouts/components/UserIcon'
import Translations from 'src/layouts/components/Translations'
import CanViewNavLink from 'src/layouts/components/acl/CanViewNavLink'

// ** Util Imports
import { hexToRGBA } from 'src/@core/utils/hex-to-rgba'
import { handleURLQueries, resolveNavPath } from 'src/@core/layouts/utils'

interface Props {
  parent?: boolean
  item: NavLink
  navHover?: boolean
  settings: Settings
  navVisible?: boolean
  collapsedNavWidth: number
  navigationBorderWidth: number
  toggleNavVisibility: () => void
  isSubToSub?: NavGroup | undefined
}

// ** Styled Components
const MenuNavLink = styled(ListItemButton)<
  ListItemButtonProps & { component?: ElementType; href: string; target?: '_blank' | undefined }
>(({ theme }) => ({
  width: '100%',
  marginLeft: theme.spacing(3.5),
  marginRight: theme.spacing(3.5),
  borderRadius: theme.shape.borderRadius,
  transition: 'padding-left .25s ease-in-out, padding-right .25s ease-in-out',
  '&:hover': {
    backgroundColor: `${theme.palette.grey[800]}`,
    '& .MuiTypography-root': {
      color: `${theme.palette.common.white}`
    },
    '& svg': {
      color: `${theme.palette.common.white}`
    }
  },
  '& svg': {
    color: `${theme.palette.common.white}`
  },
  '&.active': {
    background: '#ffd204',
    '&:hover': {
      background: `${
        theme.direction === 'ltr' ? theme.palette.secondary.main : hexToRGBA(theme.palette.secondary.main, 0.7)
      }`,
      '&.Mui-focusVisible': {
        background: `${
          theme.direction === 'ltr' ? theme.palette.secondary.main : hexToRGBA(theme.palette.secondary.main, 0.7)
        }`
      },
      '& .MuiTypography-root': {
        color: `${theme.palette.common.white}`
      },
      '& svg': {
        color: `${theme.palette.common.white}`
      }
    },
    '& .MuiTypography-root': {
      color: `${theme.palette.text.primary}`
    },
    '& svg': {
      color: `${theme.palette.text.primary}`
    }
  }
}))

const MenuItemTextMetaWrapper = styled(Box)<BoxProps>(({ theme }) => ({
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  justifyContent: 'space-between',
  transition: 'opacity .25s ease-in-out',
  ...(themeConfig.menuTextTruncate && { overflow: 'hidden' })
}))

const VerticalNavLink = ({
  item,
  parent,
  navHover,
  settings,
  navVisible,
  isSubToSub,
  collapsedNavWidth,
  toggleNavVisibility,
  navigationBorderWidth
}: Props) => {
  // ** Hooks
  const router = useRouter()
  const { id } = router.query

  // ** Vars

  const { navCollapsed } = settings

  const icon = parent && !item.icon ? themeConfig.navSubItemIcon : item.icon

  // Resolve navigation path, replacing [id] placeholder if valid
  const path: string = resolveNavPath(item.path, id as string | undefined)

  const isNavLinkActive = () => {
    // Early return if no path
    if (!path) return false

    const currentPath = router.asPath

    if (router.asPath.includes('/app/products/') && path.includes('/app/products/')) {
      return true
    }

    // More precise path matching to avoid partial matches
    // Exact path match
    if (router.pathname === path) return true

    // Handle URL queries
    if (handleURLQueries(router, path)) return true

    // For nested routes, ensure exact match or proper nesting
    if (path === '/app/restaurant' && currentPath === '/app/restaurant') {
      return true
    }

    if (path === '/app/restaurant/allocation' && currentPath.startsWith('/app/restaurant/allocation')) {
      return true
    }

    // User management specific handling
    if (path === '/app/user-management' && currentPath === '/app/user-management') {
      return true
    }

    if (path === '/app/user-management/profile' && currentPath.startsWith('/app/user-management/profile')) {
      return true
    }

    if (path === '/app/user-management/list' && currentPath.startsWith('/app/user-management/list')) {
      return true
    }

    if (path === '/app/user-management/changes' && currentPath.startsWith('/app/user-management/changes')) {
      return true
    }

    // For other cases, use exact path matching
    return currentPath === path
  }

  return (
    <CanViewNavLink navLink={item}>
      <ListItem
        disablePadding
        className='nav-link'
        disabled={item.disabled || false}
        sx={{ mt: 1, px: '0 !important' }}
      >
        <MenuNavLink
          component={Link}
          {...(item.disabled && { tabIndex: -1 })}
          className={isNavLinkActive() ? 'active' : ''}
          href={path}
          {...(item.openInNewTab ? { target: '_blank' } : null)}
          onClick={e => {
            if (item.path === undefined) {
              e.preventDefault()
              e.stopPropagation()
            }
            if (navVisible) {
              toggleNavVisibility()
            }
          }}
          sx={{
            py: 2,
            ...(item.disabled ? { pointerEvents: 'none' } : { cursor: 'pointer' }),
            px: navCollapsed && !navHover ? (collapsedNavWidth - navigationBorderWidth - 22 - 28) / 8 : 4,
            '& .MuiTypography-root, & svg': {
              color: 'common.white'
            }
          }}
        >
          <ListItemIcon
            sx={{
              transition: 'margin .25s ease-in-out',
              ...(navCollapsed && !navHover ? { mr: 0 } : { mr: 2 }),
              ...(parent ? { ml: 1.5, mr: 3.5 } : {}), // This line should be after (navCollapsed && !navHover) condition for proper styling
              '& svg': {
                fontSize: '1.375rem',
                ...(!parent ? { fontSize: '1.375rem' } : {}),
                ...(parent && item.icon ? { fontSize: '1.375rem' } : {})
              }
            }}
          >
            {!parent && <UserIcon icon={icon as string} />}
          </ListItemIcon>

          <MenuItemTextMetaWrapper
            sx={{
              ...(isSubToSub ? { ml: 2 } : {})
            }}
          >
            <Typography
              variant='body1'
              {...((themeConfig.menuTextTruncate || (!themeConfig.menuTextTruncate && navCollapsed && !navHover)) && {
                noWrap: true
              })}
            >
              <Translations text={item.title} />
            </Typography>
            {item.badgeContent ? (
              <Chip
                size='small'
                label={item.badgeContent}
                color={item.badgeColor || 'primary'}
                sx={{ height: 22, minWidth: 22, '& .MuiChip-label': { px: 1.5, textTransform: 'capitalize' } }}
              />
            ) : null}
          </MenuItemTextMetaWrapper>
        </MenuNavLink>
      </ListItem>
    </CanViewNavLink>
  )
}

export default VerticalNavLink
